import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { createAdminApi, isSessionError } from 'services/admin';

/** Separada da sessão das lojas ('session'): o login de loja não abre o painel e vice-versa. */
const STORAGE_KEY = 'efw_admin_session';

const loadSession = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        return parsed?.token && parsed?.user ? parsed : null;
    } catch (e) {
        return null;
    }
};

const AdminSessionContext = createContext(null);

export const AdminSessionProvider = ({ children }) => {
    const [session, setSession] = useState(loadSession);

    const login = useCallback((token, user) => {
        const data = { token, user: { id: user.id, name: user.name, email: user.email } };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        setSession(data);
    }, []);

    /** Atualiza nome/e-mail mostrados no painel depois de "Meu perfil", mantendo o mesmo token. */
    const updateUser = useCallback((user) => {
        setSession((current) => {
            if (!current) return current;
            const data = { ...current, user: { ...current.user, name: user.name, email: user.email } };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
            return data;
        });
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        setSession(null);
    }, []);

    const signOut = useCallback(async () => {
        if (session) await createAdminApi(session.token).logout();
        logout();
    }, [session, logout]);

    const api = useMemo(() => {
        if (!session) return null;
        const raw = createAdminApi(session.token);
        const guarded = {};
        Object.keys(raw).forEach((name) => {
            guarded[name] = async (...args) => {
                const response = await raw[name](...args);
                if (isSessionError(response)) logout();
                return response;
            };
        });
        return guarded;
    }, [session, logout]);

    const value = useMemo(() => ({ session, login, logout, signOut, updateUser, api }), [session, login, logout, signOut, updateUser, api]);

    return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
};

AdminSessionProvider.propTypes = {
    children: PropTypes.node
};

export const useAdminSession = () => useContext(AdminSessionContext);
